import instance from './axiosInstance';

// Reusable AI-alert notification rules (allow/deny, dimension + metric
// conditions, time window, attachable to many channels). The server derives the
// product context from the `mode` query param (set globally on the axios
// instance), so no context is sent in the request body.
const routerPrefix = 'notification-channel/ai-alert-rules';

export type RuleType = 'allow' | 'deny';
export type ConditionType = 'dimension' | 'metric';
export type MatchMode = 'has_dimension' | 'dimension_equals' | 'dimension_contains';
export type ChannelType = 'email' | 'webhook' | 'pagerduty' | 'datadog' | 'bigpanda' | 'googlechat';

export interface RuleConditionPayload {
  type: ConditionType;
  matchMode?: MatchMode;
  dimensionKey?: string;
  dimensionValue?: string;
  dimensionLabel?: string | null;
  metricKey?: string;
}

export interface RuleChannelPayload {
  channelType: ChannelType;
  target?: string | null;
  configId?: number | null;
}

export interface AlertRulePayload {
  name: string;
  ruleType: RuleType;
  activeFrom: string;
  activeUntil: string;
  conditions: RuleConditionPayload[];
  channels: RuleChannelPayload[];
}

export interface AlertRuleConditionRow {
  id: number;
  ruleId: number;
  conditionType: ConditionType;
  matchMode: MatchMode | null;
  dimensionKey: string | null;
  dimensionValue: string | null;
  dimensionLabel: string | null;
  metricKey: string | null;
}

export interface AlertRuleChannelRow {
  id: number;
  ruleId: number;
  channelType: ChannelType;
  target: string | null;
  configId: number | null;
}

export interface AlertRuleRow {
  id: number;
  customerId: number;
  context: string;
  name: string;
  ruleType: RuleType;
  activeFrom: string;
  activeUntil: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  createdBy: number;
  updatedBy?: number | null;
  createdByName?: string | null;
  createdByEmail?: string | null;
  status: 'active' | 'scheduled' | 'expired';
  conditions: AlertRuleConditionRow[];
  channels: AlertRuleChannelRow[];
}

export const fetchAlertRulesList = async (params?: { status?: string; channel?: string }) => {
  return instance.get(`${routerPrefix}/list`, params ? { params } : undefined);
};

export const createAlertRule = async (payload: AlertRulePayload) => {
  return instance.post(routerPrefix, payload);
};

export const updateAlertRule = async (id: number, payload: AlertRulePayload) => {
  return instance.put(`${routerPrefix}/${id}`, payload);
};

export const removeAlertRule = async (id: number) => {
  return instance.delete(`${routerPrefix}/${id}`);
};

export const attachAlertRuleChannel = async (id: number, payload: RuleChannelPayload) => {
  return instance.post(`${routerPrefix}/${id}/channels`, payload);
};

export const detachAlertRuleChannel = async (id: number, attachmentId: number) => {
  return instance.delete(`${routerPrefix}/${id}/channels/${attachmentId}`);
};

export type AlertRuleHistoryAction =
  | 'created'
  | 'updated'
  | 'deleted'
  | 'channel_attached'
  | 'channel_detached';

export interface AlertRuleFieldChange<T = string> {
  from: T;
  to: T;
}

// Computed diff attached to `updated` / channel history entries. Every field is
// optional — only changed fields are present.
export interface AlertRuleChangedFields {
  name?: AlertRuleFieldChange;
  ruleType?: AlertRuleFieldChange<RuleType>;
  activeFrom?: AlertRuleFieldChange;
  activeUntil?: AlertRuleFieldChange;
  conditionsAdded?: AlertRuleConditionRow[];
  conditionsRemoved?: AlertRuleConditionRow[];
  channelsAdded?: AlertRuleChannelRow[];
  channelsRemoved?: AlertRuleChannelRow[];
}

export interface AlertRuleHistoryEntry {
  id: number;
  ruleId: number;
  version: number;
  action: AlertRuleHistoryAction;
  name: string;
  ruleType: RuleType;
  activeFrom: string;
  activeUntil: string;
  conditions: AlertRuleConditionRow[];
  channels: AlertRuleChannelRow[];
  changedFields?: AlertRuleChangedFields | null;
  changedByName?: string | null;
  changedByEmail?: string | null;
  changedAt: string;
}

export const fetchAlertRuleHistory = async (id: number) => {
  return instance.get(`${routerPrefix}/${id}/history`);
};
